import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LayoutService } from '../../service/layout.service';
import { MenuitemComponent } from './menuitem.component';
import { CommonModule } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ConfigService } from '../../../../core/services/config.service';
import { MENU_PERM_CONFIG } from './menu-perm.config';
import { AuthService } from '../../../../core/services/auth.service';
import { filter, firstValueFrom, of, Subscription, take } from 'rxjs';
import { PermissionService } from '../../../../core/services/permission.service';
import { Permission } from '../../../../core/enums/permission.enum';
import { canViewNotifications } from '../../../../core/utils/notification-access';

import { environment } from '../../../../../environments/environment';
import { PermissionBit } from '../../../../core/utils/permission-types';
import { TranslateModule } from '@ngx-translate/core';
import { MenuService } from './menu.service';
import { ScrollFadeDirective } from './scroll-fade.directive';
import { TooltipModule } from 'primeng/tooltip';
import { shortcutLabel } from '../command-palette/keyboard-shortcuts';
import { buildMenuTree, collectBranchKeys, findActivePath, findActiveTrail, MenuNode } from './menu-tree';

const ACTIVE_ROUTE_MATCH = { paths: 'subset', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' } as const;
const FLYOUT_GAP = 12;
const FLYOUT_CLOSE_DELAY_MS = 150;

/** Section shown next to the rail. Opened by hover (transient) or by click/keyboard (pinned until dismissed). */
interface RailFlyout {
  node: MenuNode;
  trigger: HTMLElement;
  pinned: boolean;
  top: number;
  start: number;
  maxHeight: number;
}

@Component({
  selector: 'app-menu',
  standalone: true,
  templateUrl: './menu.component.html',
  imports: [CommonModule, RouterModule, MenuitemComponent, TranslateModule, ScrollFadeDirective, TooltipModule],
})
export class MenuComponent implements OnInit, OnDestroy {
  nodes: MenuNode[] = [];

  /** Shortcut that toggles expand all / collapse all (shown in the tools' tooltips). */
  readonly expandCollapseShortcut = shortcutLabel('I');

  flyout: RailFlyout | null = null;

  @ViewChild('flyoutPanel') flyoutPanel?: ElementRef<HTMLElement>;

  private navigationSubscription?: Subscription;

  private flyoutCloseTimer?: ReturnType<typeof setTimeout>;

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    private configService: ConfigService,
    private auth: AuthService,
    private perm: PermissionService,
    private menuService: MenuService
  ) { }

  ngOnInit() {
    // The session initializer has normally loaded the permissions already: only fetch them when it has not.
    const ensure$ = this.perm.isReady() ? of(null) : this.auth.loadUserInfo().pipe(take(1));

    firstValueFrom(ensure$)
      .then(() => this.fetchAppTypeAndBuildMenu())
      .catch(() => this.fetchAppTypeAndBuildMenu());

    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeFlyout();
        this.syncActiveTrail();
      });
  }

  ngOnDestroy() {
    this.navigationSubscription?.unsubscribe();
    clearTimeout(this.flyoutCloseTimer);
  }

  /** Desktop sidebar collapsed to icons (the mobile drawer always shows the full tree). */
  get rail(): boolean {
    return this.layoutService.state.staticMenuDesktopInactive && this.layoutService.isDesktop();
  }

  /** Full copyright shown above the rail's "© year" (fixed, so the rail's overflow cannot clip it). */
  copyrightTip: { start: number; bottom: number } | null = null;

  showCopyrightTip(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) return;
    const rect = target.getBoundingClientRect();
    const rtl = getComputedStyle(target).direction === 'rtl';
    this.copyrightTip = {
      // Aligned with the sign's leading edge, growing towards the page.
      start: rtl ? window.innerWidth - rect.right : rect.left,
      bottom: window.innerHeight - rect.top + 8,
    };
  }

  /** "© 2026 NTT DATA Morocco" -> "© 2026": what fits in the rail (the full text is in its tooltip). */
  copyrightMark(copyright: string): string {
    return copyright.match(/©\s*\d{4}/)?.[0] ?? '©';
  }

  isInActiveTrail(node: MenuNode): boolean {
    return this.menuService.activeTrail().has(node.key);
  }

  /** The only page of a section that has a single page: its rail icon links straight to it. */
  singlePage(section: MenuNode): MenuNode | null {
    const [only, ...others] = section.children;
    return only && others.length === 0 && only.children.length === 0 ? only : null;
  }

  openFlyout(node: MenuNode, trigger: EventTarget | null, pinned = false) {
    clearTimeout(this.flyoutCloseTimer);
    if (!(trigger instanceof HTMLElement)) return;
    if (this.flyout?.node === node) {
      this.flyout.pinned ||= pinned;
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const rtl = getComputedStyle(trigger).direction === 'rtl';
    // Aligned with the icon, nudged up near the bottom so the panel keeps room for its pages.
    const top = Math.max(FLYOUT_GAP, Math.min(rect.top - 8, window.innerHeight - 360));
    this.flyout = {
      node,
      trigger,
      pinned,
      top,
      start: rtl ? window.innerWidth - rect.left + FLYOUT_GAP : rect.right + FLYOUT_GAP,
      maxHeight: window.innerHeight - top - FLYOUT_GAP,
    };
  }

  /** Click / Enter on a rail icon: pins the section open, or closes it when already pinned. */
  toggleFlyout(node: MenuNode, event: MouseEvent) {
    if (this.flyout?.node === node && this.flyout.pinned) {
      this.closeFlyout();
      return;
    }
    this.openFlyout(node, event.currentTarget, true);
    // Keyboard activation (no pointer): move focus into the panel so its pages are reachable.
    if (event.detail === 0) {
      setTimeout(() => this.flyoutPanel?.nativeElement.querySelector<HTMLElement>('a, button')?.focus());
    }
  }

  scheduleFlyoutClose() {
    if (!this.flyout || this.flyout.pinned) return;
    clearTimeout(this.flyoutCloseTimer);
    this.flyoutCloseTimer = setTimeout(() => this.closeFlyout(), FLYOUT_CLOSE_DELAY_MS);
  }

  cancelFlyoutClose() {
    clearTimeout(this.flyoutCloseTimer);
  }

  closeFlyout(restoreFocus = false) {
    clearTimeout(this.flyoutCloseTimer);
    if (restoreFocus) this.flyout?.trigger.focus();
    this.flyout = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.flyout) return;
    // Path captured at dispatch: still right when the clicked element was re-rendered since.
    const path = event.composedPath();
    if (path.includes(this.flyout.trigger) || (this.flyoutPanel && path.includes(this.flyoutPanel.nativeElement))) return;
    this.closeFlyout();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.flyout) this.closeFlyout(true);
  }

  expandAll() {
    this.menuService.setExpanded(collectBranchKeys(this.nodes), true);
  }

  collapseAll() {
    this.menuService.setExpanded(collectBranchKeys(this.nodes), false);
  }

  trackByKey(_: number, node: MenuNode): string {
    return node.key;
  }

  private setMenu(model: MenuItem[]) {
    this.nodes = buildMenuTree(model);
    this.menuService.tree.set(this.nodes);
    // A fresh menu always opens the current branch, before the first render so it shows without animating.
    this.menuService.activeTrail.set(new Set());
    this.syncActiveTrail();
  }

  /**
   * Highlights the branches leading to the current route and opens the ones just entered.
   * Branches already on the trail are left alone, so a branch the user collapsed stays
   * collapsed while they move around inside it (filters, query params, sibling pages).
   */
  private syncActiveTrail() {
    const previous = this.menuService.activeTrail();
    const isActive = (routerLink: any[]) => this.router.isActive(this.router.createUrlTree(routerLink), ACTIVE_ROUTE_MATCH);
    const trail = findActiveTrail(this.nodes, isActive);
    this.menuService.activeTrail.set(trail);
    this.menuService.activePath.set(findActivePath(this.nodes, isActive));
    this.menuService.setExpanded([...trail].filter((key) => !previous.has(key)), true);
  }

  private fetchAppTypeAndBuildMenu() {
    this.configService.getAppType().subscribe({
      next: (appType) => {
        this.setMenu(this.getMenuModel(appType));
        this.redirectToInitialPage(appType);
      },
      error: (err) => {
        const defaultAppType = 'hr';
        this.setMenu(this.getMenuModel(defaultAppType));
        this.redirectToInitialPage(defaultAppType);
      },
    });
  }

  private can(bits: bigint[] | undefined): boolean {
    if (!bits || bits.length === 0) return true;
    return this.perm.hasAnyPermission(bits);
  }

  getMenuModel(appType: string | null): MenuItem[] {
    const canAny = (key: keyof typeof MENU_PERM_CONFIG) => this.can(MENU_PERM_CONFIG[key]);

    /*const hasPermission = (p: PermissionBit) => this.perm.hasPermission(p);
    const canViewNotificationPages = canViewNotifications(hasPermission);*/

    const dashboard = [
      {
        label: 'layout.sidebar.titles.dashboard',
        icon: 'pi pi-fw pi-th-large',
        visible: canAny('dashboard') && (environment as any)['showDashboard'] !== false,
        items: [
          { label: 'layout.sidebar.dashboard', icon: 'pi pi-fw pi-chart-bar', routerLink: ['/staffing/dashboard'], },
        ],
      }
    ];
    const employeeMenuItems = [
      {
        label: 'layout.sidebar.cardsView',
        icon: 'pi pi-fw pi-th-large',
        routerLink: ['/employees/card-view'],
        visible: canAny('cardsView') && (environment as any)['showCardsView'] !== false,
      },
      {
        label: 'layout.sidebar.listView',
        icon: 'pi pi-fw pi-list',
        routerLink: ['/employees/grid-view'],
        visible: canAny('employeesListView') && (environment as any)['showListView'] !== false,
      },
            {
        label: 'layout.sidebar.interns',
        icon: 'pi pi-fw pi-user',
        routerLink: ['/employees/interns'],
        visible: canAny('interns'),
      },
      {
        label: 'layout.sidebar.validation',
        icon: 'pi pi-fw pi-check-square',
        visible: canAny('employeeValidation') && (environment as any)['showValidationMenu'] !== false,
        items: [
          { label: 'layout.sidebar.roleValidation', icon: 'pi pi-fw pi-users', routerLink: ['/employees/role-validation'], visible: canAny('roleValidation'), },
          { label: 'layout.sidebar.salaryValidation', icon: 'pi pi-fw pi-wallet', routerLink: ['/employees/salary-validation'], visible: canAny('salaryValidation'), },
        ],
      },
      {
        label: 'layout.sidebar.notification',
        icon: 'pi pi-fw pi-bell',
         visible: canAny('employeeNotifications'),
        items: [
          { label: 'layout.sidebar.contractEnding', icon: 'pi pi-fw pi-calendar-times', routerLink: ['/employees/contract-ending'], visible:canAny('contractEndingNotification') },
          { label: 'layout.sidebar.trialPeriod', icon: 'pi pi-fw pi-clock', routerLink: ['/employees/trial-period'], visible: canAny('trialPeriodNotification') },
          { label: 'layout.sidebar.juniorPromotion', icon: 'pi pi-fw pi-angle-up', routerLink: ['/employees/junior-promotion'], visible: canAny('juniorPromotionNotification') },
        ],
      },
    ];
    const employeeMenu = [
      {
        label: 'layout.sidebar.titles.employeesManagement',
        icon: 'pi pi-fw pi-id-card',
        visible: employeeMenuItems.some(item => item.visible),
        items: employeeMenuItems,
      }
    ];

    const staffingMenuItems = [
      {
        label: 'layout.sidebar.client',
        icon: 'pi pi-fw pi-sitemap',
        routerLink: ['/staffing/client'],
        visible: canAny('client') && (environment as any)['showClient'] !== false,
      },
      {
        label: 'layout.sidebar.project',
        icon: 'pi pi-briefcase',
        routerLink: ['/staffing/project'],
        visible: canAny('project') && (environment as any)['showProject'] !== false,
      },
      {
        label: 'layout.sidebar.staff',
        icon: 'pi pi-fw pi-users',
        routerLink: ['/staffing/staff'],
        visible: canAny('staff') && (environment as any)['showStaff'] !== false,
      },
      {
        label: 'layout.sidebar.pool',
        icon: 'pi pi-folder',
        visible: canAny('pool') && (environment as any)['showPoolMenu'] !== false,
        items: [
          { label: 'layout.sidebar.listView', icon: 'pi pi-list', routerLink: ['/pool-management/grid-view'] },
          { label: 'layout.sidebar.activities', icon: 'pi pi-calendar', routerLink: ['/pool-management/activities'] },
          { label: 'layout.sidebar.report', icon: 'pi pi-chart-pie', routerLink: ['/pool-management/report'] },
        ],
      },
    ];
    const staffingMenu = [
      {
        label: 'layout.sidebar.titles.staffing',
        icon: 'pi pi-fw pi-users',
        visible: canAny('staffing') && staffingMenuItems.some(item => item.visible),
        items: staffingMenuItems,
      }
    ];

    const financeMenuItems = [
      {
        label: 'layout.sidebar.group',
        icon: 'pi pi-fw pi-users',
        routerLink: ['/finance/group'],
        visible: canAny('group') && (environment as any)['showGroup'] !== false,
      },
      {
        label: 'layout.sidebar.process',
        icon: 'pi pi-fw pi-sitemap',
        routerLink: this.getProcessRoute(),
        visible: canAny('process') && (environment as any)['showProcess'] !== false,
      },
    ];
    const financeMenu = [
      {
        label: 'layout.sidebar.titles.finance',
        icon: 'pi pi-fw pi-wallet',
        visible: canAny('finance') && financeMenuItems.some(item => item.visible),
        items: financeMenuItems,
      }
    ];

    const productionMenuItems = [
      {
        label: 'layout.sidebar.service',
        icon: 'pi pi-fw pi-briefcase',
        routerLink: ['/production/service'],
        visible: canAny('productionService'),
      },
      {
        label: 'layout.sidebar.productionUnsigned',
        icon: 'pi pi-fw pi-user-minus',
        routerLink: ['/production/unsigned'],
        visible: canAny('productionUnsigned'),
      },
      {
        label: 'layout.sidebar.productionWithSupervisor',
        icon: 'pi pi-fw pi-users',
        routerLink: ['/production/with-supervisor'],
        visible: canAny('productionWithSupervisor'),
      },
    ];
    const productionMenu = [
      {
        label: 'layout.sidebar.titles.production',
        icon: 'pi pi-fw pi-box',
        visible: canAny('production') && productionMenuItems.some(item => item.visible),
        items: productionMenuItems,
      }
    ];

    const performanceMenuItems = [
      {
        label: 'layout.sidebar.promotion',
        icon: 'pi pi-arrow-up',
        routerLink: ['/staffing/promotions'],
        visible: canAny('promotion') && (environment as any)['showPromotion'] !== false,
      },
      {
        label: 'layout.sidebar.evaluation',
        icon: 'pi pi-file-edit',
        routerLink: this.perm.hasPermission(Permission.EVALUATION_READ)
          ? ['/staffing/evaluation-employee']
          : undefined,
        visible: (canAny('evaluation') || canAny('evaluation_review_impact')) && (environment as any)['showEvaluationMenu'] !== false,
        items: [
          { label: 'layout.sidebar.campaigns', icon: 'pi pi-chart-pie', routerLink: ['/staffing/evaluation-employee/campaign-list'], visible: this.can(MENU_PERM_CONFIG['evaluation_campaigns']) },
          { label: 'layout.sidebar.myEvaluations', icon: 'pi pi-book', routerLink: ['/staffing/evaluation-employee/evaluation-employee-list'], visible: this.can(MENU_PERM_CONFIG['evaluation_my']) },
          { label: 'layout.sidebar.mySelfEvaluation', icon: 'pi pi-user-edit', routerLink: ['/staffing/evaluation-employee/self-evaluation-list'], visible: this.can(MENU_PERM_CONFIG['evaluation_self']) },
          { label: 'layout.sidebar.assignReviewers', icon: 'pi pi-user-plus', routerLink: ['/staffing/evaluation-employee/assign-reviewers'], visible: this.can(MENU_PERM_CONFIG['evaluation_assign']) },
          { label: 'layout.sidebar.remuneration', icon: 'pi pi-wallet', routerLink: ['/staffing/evaluation-employee/remuneration'], visible: this.can(MENU_PERM_CONFIG['evaluation_remuneration']) },
          { label: 'layout.sidebar.reviewImpact', icon: 'pi pi-calculator', routerLink: ['/staffing/evaluation-employee/review-impact'], visible: this.can(MENU_PERM_CONFIG['evaluation_review_impact']) },
        ],
      },
    ];
    const performanceManagementMenu = [
      {
        label: 'layout.sidebar.titles.performanceManagement',
        icon: 'pi pi-fw pi-chart-line',
        visible: (canAny('performanceManagement') || canAny('evaluation_review_impact')) && performanceMenuItems.some((item) => item.visible),
        items: performanceMenuItems,
      },
    ];

    const feedbackMenu = [
      {
        label: 'layout.sidebar.titles.feedback',
        icon: 'pi pi-fw pi-comments',
        visible: this.auth.isLoggedIn(),
        items: [
          {
            label: 'layout.sidebar.feedback',
            icon: 'pi pi-fw pi-comments',
            routerLink: ['/feedback'],
            visible: true,
          },
        ],
      },
    ];

    const bsaOrgMenuItems = [
      {
        label: 'layout.sidebar.organisation',
        icon: 'pi pi-fw pi-sitemap',
        routerLink: ['/bsa-team/organisation'],
        visible: (environment as any)['showOrganisation'] !== false,
      },
      {
        label: 'layout.sidebar.posts',
        icon: 'pi pi-fw pi-building',
        routerLink: ['/bsa-team/posts'],
        visible: canAny('bsaOrgWrite') && (environment as any)['showPosts'] !== false,
      },
      {
        label: 'layout.sidebar.tasks',
        icon: 'pi pi-fw pi-check-square',
        routerLink: ['/bsa-team/tasks'],
        visible: canAny('bsaOrgWrite') && (environment as any)['showTasks'] !== false,
      },
    ];
    
    const bsaOrgMenu = [
      {
        label: 'layout.sidebar.titles.bsaOrg',
        icon: 'pi pi-fw pi-sitemap',
        visible: canAny('bsaOrg') && bsaOrgMenuItems.some(item => item.visible),
        items: bsaOrgMenuItems,
      }
    ];

    const auditLogMenu = [
      {
        label: 'layout.sidebar.titles.auditLog',
        icon: 'pi pi-fw pi-history',
        visible: canAny('audit_log') && (environment as any)['showAuditLogMenu'] !== false,
        items: [
          { label: 'layout.sidebar.history', icon: 'pi pi-history', routerLink: ['/audit-log'] }
        ]
      }
    ];

    const opportunitiesMenu = [
      {
        label: 'opportunities.menu.title',
        icon: 'pi pi-fw pi-briefcase',
        visible: canAny('opportunities'),
        items: [
          {
            label: 'opportunities.menu.opportunities',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/opportunities/opportunities-list'],
            visible: canAny('opportunitiesList')
          },
          {
            label: 'opportunities.menu.demandes',
            icon: 'pi pi-fw pi-folder',
            routerLink: ['/opportunities/demandes'],
            visible: canAny('demandes')
          },
          {
            label: 'opportunities.menu.peopleAtRisk',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/opportunities/release'],
            visible: canAny('release')
          },
          {
            label: 'opportunities.menu.badAttitude',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/opportunities/bad-attitude'],
            visible: canAny('badAttitude')
          }
        ]
      }
    ];

    if (appType === 'staffing') {
      return [...staffingMenu, ...opportunitiesMenu, ...productionMenu, ...financeMenu, ...feedbackMenu, ...auditLogMenu];
    } else if (appType === 'hr') {
      return [...employeeMenu, ...staffingMenu, ...opportunitiesMenu, ...productionMenu, ...financeMenu, ...feedbackMenu, ...auditLogMenu];
    } else {
      // Default to development mode showing all menus
      return [
        ...dashboard,
        ...employeeMenu,
        ...staffingMenu,
        ...opportunitiesMenu,
        ...productionMenu,
        ...bsaOrgMenu,
        ...performanceManagementMenu,
        ...financeMenu,
        ...feedbackMenu,
        ...auditLogMenu,
      ];
    }
  }

  private getProcessRoute(): string[] {
    return ['/finance/admin-process'];
  }

  redirectToInitialPage(appType: string | null) {
    if (this.router.url !== '/' && this.router.url !== '') {
      return;
    }

    if (appType === 'staffing') {
      this.router.navigate(['/staffing/dashboard']);
    } else if (appType === 'hr') {
      if (this.perm.hasPermission(Permission.CARDS_VIEW_READ)) {
        this.router.navigate(['/employees/card-view']);
      } } else if (
  this.perm.hasPermission(Permission.DASHBOARD_GLOBAL) ||
  this.perm.hasPermission(Permission.DASHBOARD_CLIENT) ||
  this.perm.hasPermission(Permission.DASHBOARD_LEADER) ||
  this.perm.hasPermission(Permission.DASHBOARD_LANGUAGE) ||
  this.perm.hasPermission(Permission.DASHBOARD_PROMOTION)
) {
  this.router.navigate(['/staffing/dashboard']);
}

  }
}
